
import { completeStopFromDashboard } from '@/api';
import Button from '@/components/UI/button/Button.vue';
import store from '@/store';
import { Job, StatusChartItem, StopCompleteParams } from '@/models/main';
import { format } from 'date-fns';
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import { notifyNetworkError } from '@/helpers/notifiers';

@Component({
  components: {
    Button,
  },
})
export default class KStatusChart extends Vue {
  @Prop(Object)
  public job!: Job;

  @Prop(Object)
  public jobs!: Job | undefined;

  @Prop(String)
  public jobDate!: string;

  @Prop(Boolean)
  public showEdit!: boolean;

  @Prop(Array)
  private statusChartLabels!: string[];

  public width = 440;
  public height = 50;
  public r = 10;
  public step = 64;
  public lightGray = '#e0b4b8';
  public gray = '#bdbdbd';
  public green = '#43a047';
  public red = '#d32f2f';
  public orange = '#f57c00';
  public blue = '#1976d2';
  public labelShowed = false;
  public labelText = '';
  public labelX = 0;
  public labelY = 0;
  public labelFontSize = 11;
  public indexList: string[] = ['booked', 'allocated', 'departed1', 'arrived2', 'pod_time2', 'completed', 'cancelled'];

  public jobModalData = {
    isOpen: false,
    action: '',
    position: '',
    jobIdShort: 0,
    jobId: 0,
    jobDate: '',
    driver: 0,
    actionText: '',
    stopText: '',
    stopNumber: 0,
    stopFromName: '',
    stopToName: '',
    iconPosition: 0,
    isMainStop: false,
    podName: '',
    error: '',
    loading: false,
    has_3rd_stop: false,
  };

  public showLabel(i: number): void {
    this.labelShowed = true;
    this.labelText = this.statusList[i - 1].label;
    // + 15 is a margin from left
    this.labelX = (i - 1) * this.step + this.r / 2 - this.statusList[i - 1].label.length * 2 + 15;
    this.labelY = this.height / 2 + 22;
  }

  public get statusList(): StatusChartItem[] {
    const items: StatusChartItem[] = [];
    for (let i = 0; i < 6; i++) {
      const item: StatusChartItem = {
        date: '',
        color: '',
        label: '',
        num: i + 1,
      };
      if (i <= this.job.status) {
        item.date = this.job[this.indexList[i]];
        item.color = this.green;
        item.label = this.statusChartLabels[i];
      } else {
        item.date = '';
        item.color = this.gray;
        item.label = this.statusChartLabels[i];
        if (i === this.job.status + 1 && (this.job.status === 1 || this.job.status === 2)) {
          if (this.job.delay === 1 && this.$ability.can('staff', 'Job')) {
            item.color = this.orange;
          } else if (this.job.delay === 2) {
            item.color = this.red;
          }
        }
      }
      if (i === 1) {
        if (this.job.equipment_code1 === '' && this.job.allocated === 'None' && this.job.arrived1 === 'None') {
          if (format(new Date(), 'yyyy-MM-dd') === format(new Date(this.job.required2), 'yyyy-MM-dd')) {
            item.color = this.red;
          } else {
            item.color = this.blue;
          }
        }
        if (this.job.status < 2) {
          item.date = this.job.assigned1 || this.job.allocated;
        } else {
          item.date = this.job.assigned2 || this.job.allocated;
        }
      }
      if (i === 2 && this.job.departed1 === 'None' && this.job.required1 !== 'None') {
        item.date = this.job.required1;
        // item.label = 'Stop1(required)';
      }
      if (
        this.$ability.can('staff', 'Job') &&
        i === 3 &&
        this.job.arrived2 === 'None' &&
        this.job.required2 !== 'None'
      ) {
        item.date = this.job.required2;
        // item.label = 'Stop2(required)';
        if (
          format(new Date(), 'yyyy-MM-dd') < format(new Date(this.job.required2), 'yyyy-MM-dd') &&
          this.job.status === 2
        ) {
          item.color = this.blue;
        }
      }
      if (
        i === 3 &&
        this.job.main_stop === 1 &&
        this.job.departed1 !== 'None' &&
        this.job.arrived2 === 'None' &&
        this.job.status < 4
      ) {
        item.color = this.gray;
      }
      if (i === 5 && this.job.completed === 'None') {
        item.label = 'Not Completed';
      }
      if (this.job.status === 6) {
        item.date = this.job[this.indexList[6]];
        item.label = this.statusChartLabels[6];
        item.color = this.red;
      }
      items.push(item);
    }
    return items;
  }

  // JOB COMPLETE MODAL
  public toggleModal(label): void {
    if (this.$ability.can('staff', 'Job') && !this.job.cancelled && !this.job.completed) {
      if (!this.job.allocated) {
        notifyNetworkError({ text: "Can't arrive/depart not allocated job" });
        return;
      }
      // main_stop = 1 – going from client to KKTS, 2 – from client to KKTS warehouse
      // STOP 1 ARRIVED
      if (label === 'Stop1' && !this.job.arrived1) {
        this.jobModalData = {
          ...this.jobModalData,
          isMainStop: false,
          action: 'arrived',
          position: 'Stop1',
          stopNumber: 1,
          jobIdShort: Number(this.job.id),
          jobId: Number(this.job.job_key),
          jobDate: this.job.job_date,
          driver: Number(this.job.driver1),
          actionText: 'arrived to',
          stopText: 'Stop 1',
          stopFromName: this.job.stop1_name,
          stopToName: this.job.stop2_name,
          iconPosition: 1,
          isOpen: true,
        };
      }
      // STOP 1 DEPARTED
      if (label === 'Stop1' && this.job.arrived1 && !this.job.departed1) {
        if (this.job.main_stop === 1 && this.job.require_pod) {
          this.jobModalData.isMainStop = true;
        }
        this.jobModalData = {
          ...this.jobModalData,
          action: 'departed',
          position: 'Stop1',
          stopNumber: 1,
          jobIdShort: Number(this.job.id),
          jobId: Number(this.job.job_key),
          jobDate: this.job.job_date,
          driver: Number(this.job.driver1),
          actionText: 'departed from',
          stopText: 'Stop 1',
          stopFromName: this.job.stop1_name,
          stopToName: this.job.stop2_name,
          iconPosition: 2,
          isOpen: true,
        };
      }
      // STOP 2 ARRIVED
      if (label === 'Stop2' && !this.job.arrived2) {
        this.jobModalData = {
          ...this.jobModalData,
          isMainStop: false,
          action: 'arrived',
          position: 'Stop2',
          stopNumber: 2,
          jobIdShort: Number(this.job.id),
          jobId: Number(this.job.job_key),
          jobDate: this.job.job_date,
          driver: Number(this.job.driver2),
          actionText: 'arrived to',
          stopText: 'Stop 2',
          stopFromName: this.job.stop1_name,
          stopToName: this.job.stop2_name,
          iconPosition: 3,
          isOpen: true,
        };
      }
      // STOP 2 DEPARTED
      if (label === 'Stop2' && this.job.arrived2) {
        if (this.job.main_stop === 2 && this.job.require_pod) {
          this.jobModalData.isMainStop = true;
        }
        this.jobModalData = {
          ...this.jobModalData,
          action: 'departed',
          position: 'Stop2',
          stopNumber: 2,
          jobIdShort: Number(this.job.id),
          jobId: Number(this.job.job_key),
          driver: Number(this.job.driver2),
          jobDate: this.job.job_date,
          actionText: 'departed from',
          stopText: 'Stop 2',
          stopFromName: this.job.stop1_name,
          stopToName: this.job.stop2_name,
          iconPosition: 4,
          isOpen: true,
        };
      }
      // COMPLETED
      if (label === 'Completed') {
        if (this.job.require_pod) {
          this.jobModalData.isMainStop = true;
        }
        this.job.has_3rd_stop ? (this.jobModalData.stopNumber = 3) : (this.jobModalData.stopNumber = 2);
        this.jobModalData = {
          ...this.jobModalData,
          action: 'departed',
          position: 'Completed',
          jobIdShort: Number(this.job.id),
          jobId: Number(this.job.job_key),
          jobDate: this.job.job_date,
          driver: Number(this.job.driver2),
          actionText: 'complete',
          stopText: 'all the stops',
          stopFromName: this.job.stop1_name,
          stopToName: this.job.stop2_name,
          iconPosition: 4,
          isOpen: true,
        };
      }
    }
  }

  // JOB COMPLETE
  public completeStop(): void {
    const timeStamp = this.job.booked;
    this.jobModalData.loading = true;
    const action = this.jobModalData.action;

    const updateTrafficLight = () => {
      this.job.delay = 0;
      if (this.jobModalData.action === 'arrived' && this.jobModalData.stopNumber === 1) {
        this.job.arrived1 = timeStamp;
        this.job.status = 2;
      }
      if (this.jobModalData.action === 'departed' && this.jobModalData.stopNumber === 1) {
        this.job.departed1 = timeStamp;
        this.job.status = 2;
      }
      if (this.jobModalData.action === 'arrived' && this.jobModalData.stopNumber === 2) {
        this.job.arrived2 = timeStamp;
        this.job.status = 3;
      }
      if (this.jobModalData.action === 'departed' && this.jobModalData.stopNumber === 2) {
        this.job.completed = timeStamp;
        this.job.status = 5;
      }
    };

    let params: StopCompleteParams = {
      driver_id: this.jobModalData.driver,
      job_id: this.jobModalData.jobIdShort,
      job_date: this.jobModalData.jobDate,
      stop_number: this.jobModalData.stopNumber,
    };

    if (this.jobModalData.isMainStop) {
      params = { ...params, pod_name: this.jobModalData.podName };
    }

    completeStopFromDashboard(action, params)
      .then(res => {
        if (res?.data.Result === 'Stop sent') {
          store.dispatch('updateJobData', this.job);
          this.jobModalData.loading = false;
          this.jobModalData.isOpen = false;
          updateTrafficLight();
        }
      })
      .catch(err => {
        this.jobModalData.error = err;
        this.jobModalData.loading = false;
        return null;
      });
  }
}
